import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { AuctionHallService } from 'src/app/services/auction-hall/auction-hall.service';
import { TransportPickupModalService } from 'src/app/services/transport-pickup-modal/transport-pickup-modal.service';
import { TransportPickupService } from 'src/app/services/transport-pickup/transport-pickup.service';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

interface RowState {
  vehicleInfo: any;
  vendorSearchTerm: string;
  vendorSearchResults: any[];
  showVendorDropdown: boolean;
  isVendorSearching: boolean;
  pickupFromSearchTerm: string;
  pickupFromSearchResults: any[];
  showPickupFromDropdown: boolean;
  isPickupFromSearching: boolean;
  deliveredAtSearchTerm: string;
  deliveredAtSearchResults: any[];
  showDeliveredAtDropdown: boolean;
  isDeliveredAtSearching: boolean;
}

@Component({
  selector: 'app-transport-pickup-bulk-update',
  templateUrl: './transport-pickup-bulk-update.component.html',
  styleUrls: ['./transport-pickup-bulk-update.component.scss']
})
export class TransportPickupBulkUpdateComponent extends DestructorComponent implements OnInit {

  @Output() bulkUpdated = new EventEmitter<void>();

  bulkForm: FormGroup;
  rowStates: RowState[] = [];
  isLoading: boolean = false;

  private rowSearchTimeouts: { [key: string]: ReturnType<typeof setTimeout> } = {};

  constructor(
    private fb: FormBuilder,
    private _transportPickupService: TransportPickupService,
    private _transportPickupModalService: TransportPickupModalService,
    private _vendorService: VendorService,
    private _auctionHallService: AuctionHallService,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.bulkForm = this.fb.group({ items: this.fb.array([]) });
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  get itemsArray(): FormArray {
    return this.bulkForm.get('items') as FormArray;
  }

  getFormGroup(index: number): FormGroup {
    return this.itemsArray.at(index) as FormGroup;
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_transport_pickup_bulk_update');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const items = this._transportPickupModalService.getBulkItems();
        this.buildFormArray(items);
      });
      modalElement.addEventListener('hidden.bs.modal', () => {
        this._transportPickupModalService.clearBulkItems();
        this.resetForm();
      });
    }
  }

  private buildFormArray(items: any[]): void {
    this.itemsArray.clear();
    this.rowStates = [];
    items.forEach(item => {
      this.itemsArray.push(this.fb.group({
        _id: [item._id || item.id],
        vehicle_id: [item.vehicle_id || ''],
        order_date: [item.order_date || ''],
        yard_in: [item.yard_in || ''],
        out_date: [item.out_date || ''],
        yard_out: [item.yard_out || ''],
        pos_no: [item.pos_no || ''],
        pickup_from: [item.pickup_from || ''],
        pickup_from_id: [item.pickup_from_id || ''],
        delivered_at: [item.delivered_at || ''],
        delivered_at_id: [item.delivered_at_id || ''],
        transport_company_id: [item.transport_company_id || ''],
        transport_company_name: [item.transport_company_name || ''],
        transport_cost: [item.transport_cost ?? null],
        comment_for_transporter: [item.comment_for_transporter || ''],
        admin_comment: [item.admin_comment || '']
      }));
      this.rowStates.push({
        vehicleInfo: item.vehicle || {},
        vendorSearchTerm: item.transport_company_name || '',
        vendorSearchResults: [],
        showVendorDropdown: false,
        isVendorSearching: false,
        pickupFromSearchTerm: item.pickup_from || '',
        pickupFromSearchResults: [],
        showPickupFromDropdown: false,
        isPickupFromSearching: false,
        deliveredAtSearchTerm: item.delivered_at || '',
        deliveredAtSearchResults: [],
        showDeliveredAtDropdown: false,
        isDeliveredAtSearching: false
      });
    });
    this.cdr.detectChanges();
  }

  private resetForm(): void {
    this.itemsArray.clear();
    this.rowStates = [];
    this.isLoading = false;
    Object.values(this.rowSearchTimeouts).forEach(t => clearTimeout(t));
    this.rowSearchTimeouts = {};
  }

  onSubmit(): void {
    this.isLoading = true;
    const payload = this.itemsArray.controls.map(ctrl => ({ ...ctrl.value }));

    this.subscriptions.push(
      this._transportPickupService.BulkUpdate(payload).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: `${payload.length} transport pickup(s) updated successfully.`,
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.bulkUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to bulk update transport pickups.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error bulk updating:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to bulk update. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_transport_pickup_bulk_update');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
  }

  // ── Vendor (Transport Company) search ──────────────────────────────────────
  onRowVendorSearchInput(event: Event, index: number): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.rowStates[index].vendorSearchTerm = searchTerm;
    this.itemsArray.at(index).patchValue({ transport_company_name: searchTerm, transport_company_id: '' });
    const key = `vendor_${index}`;
    if (this.rowSearchTimeouts[key]) clearTimeout(this.rowSearchTimeouts[key]);
    if (searchTerm.length >= 2) {
      this.rowStates[index].showVendorDropdown = true;
      this.rowSearchTimeouts[key] = setTimeout(() => this.searchRowVendors(searchTerm, index), 300);
    } else {
      this.rowStates[index].showVendorDropdown = false;
      this.rowStates[index].vendorSearchResults = [];
    }
  }

  onRowVendorInputBlur(index: number): void {
    setTimeout(() => { this.rowStates[index].showVendorDropdown = false; }, 200);
  }

  private searchRowVendors(searchTerm: string, index: number): void {
    this.rowStates[index].isVendorSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'transport_company' }).subscribe({
        next: (response: any) => {
          this.rowStates[index].isVendorSearching = false;
          if (response.status && response.data) {
            this.rowStates[index].vendorSearchResults = response.data.data || response.data || [];
            this.rowStates[index].showVendorDropdown = this.rowStates[index].vendorSearchResults.length > 0;
          } else {
            this.rowStates[index].vendorSearchResults = [];
            this.rowStates[index].showVendorDropdown = false;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.rowStates[index].isVendorSearching = false;
          this.rowStates[index].vendorSearchResults = [];
          this.rowStates[index].showVendorDropdown = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  selectRowVendor(vendor: any, index: number): void {
    const name = vendor.company_name || '';
    this.rowStates[index].vendorSearchTerm = name;
    this.rowStates[index].showVendorDropdown = false;
    this.itemsArray.at(index).patchValue({ transport_company_id: vendor._id || vendor.id, transport_company_name: name });
  }

  // ── Pickup From search ─────────────────────────────────────────────────────
  onRowPickupFromSearchInput(event: Event, index: number): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.rowStates[index].pickupFromSearchTerm = searchTerm;
    this.itemsArray.at(index).patchValue({ pickup_from: searchTerm, pickup_from_id: '' });
    const key = `pickupFrom_${index}`;
    if (this.rowSearchTimeouts[key]) clearTimeout(this.rowSearchTimeouts[key]);
    if (searchTerm.length >= 2) {
      this.rowStates[index].showPickupFromDropdown = true;
      this.rowSearchTimeouts[key] = setTimeout(() => this.searchRowPickupFrom(searchTerm, index), 300);
    } else {
      this.rowStates[index].showPickupFromDropdown = false;
      this.rowStates[index].pickupFromSearchResults = [];
    }
  }

  onRowPickupFromInputBlur(index: number): void {
    setTimeout(() => { this.rowStates[index].showPickupFromDropdown = false; }, 200);
  }

  private searchRowPickupFrom(searchTerm: string, index: number): void {
    this.rowStates[index].isPickupFromSearching = true;
    this.subscriptions.push(
      this._auctionHallService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: any) => {
          this.rowStates[index].isPickupFromSearching = false;
          if (response.status && response.data) {
            this.rowStates[index].pickupFromSearchResults = response.data.data || response.data || [];
            this.rowStates[index].showPickupFromDropdown = this.rowStates[index].pickupFromSearchResults.length > 0;
          } else {
            this.rowStates[index].pickupFromSearchResults = [];
            this.rowStates[index].showPickupFromDropdown = false;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.rowStates[index].isPickupFromSearching = false;
          this.rowStates[index].pickupFromSearchResults = [];
          this.rowStates[index].showPickupFromDropdown = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  selectRowPickupFrom(pickupFrom: any, index: number): void {
    const name = pickupFrom.name || pickupFrom.company_name || '';
    this.rowStates[index].pickupFromSearchTerm = name;
    this.rowStates[index].showPickupFromDropdown = false;
    this.itemsArray.at(index).patchValue({ pickup_from: name, pickup_from_id: pickupFrom._id || pickupFrom.id });
  }

  // ── Delivered At search ────────────────────────────────────────────────────
  onRowDeliveredAtSearchInput(event: Event, index: number): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.rowStates[index].deliveredAtSearchTerm = searchTerm;
    this.itemsArray.at(index).patchValue({ delivered_at: searchTerm, delivered_at_id: '' });
    const key = `deliveredAt_${index}`;
    if (this.rowSearchTimeouts[key]) clearTimeout(this.rowSearchTimeouts[key]);
    if (searchTerm.length >= 2) {
      this.rowStates[index].showDeliveredAtDropdown = true;
      this.rowSearchTimeouts[key] = setTimeout(() => this.searchRowDeliveredAt(searchTerm, index), 300);
    } else {
      this.rowStates[index].showDeliveredAtDropdown = false;
      this.rowStates[index].deliveredAtSearchResults = [];
    }
  }

  onRowDeliveredAtInputBlur(index: number): void {
    setTimeout(() => { this.rowStates[index].showDeliveredAtDropdown = false; }, 200);
  }

  private searchRowDeliveredAt(searchTerm: string, index: number): void {
    this.rowStates[index].isDeliveredAtSearching = true;
    this.subscriptions.push(
      this._vendorService.GetData('1', searchTerm, { paginate: false, type: 'yard' }).subscribe({
        next: (response: any) => {
          this.rowStates[index].isDeliveredAtSearching = false;
          if (response.status && response.data) {
            this.rowStates[index].deliveredAtSearchResults = response.data.data || response.data || [];
            this.rowStates[index].showDeliveredAtDropdown = this.rowStates[index].deliveredAtSearchResults.length > 0;
          } else {
            this.rowStates[index].deliveredAtSearchResults = [];
            this.rowStates[index].showDeliveredAtDropdown = false;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.rowStates[index].isDeliveredAtSearching = false;
          this.rowStates[index].deliveredAtSearchResults = [];
          this.rowStates[index].showDeliveredAtDropdown = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  selectRowDeliveredAt(deliveredAt: any, index: number): void {
    const name = deliveredAt.company_name || deliveredAt.delivered_at || '';
    this.rowStates[index].deliveredAtSearchTerm = name;
    this.rowStates[index].showDeliveredAtDropdown = false;
    this.itemsArray.at(index).patchValue({ delivered_at: name, delivered_at_id: deliveredAt._id || deliveredAt.id });
  }
}
